import React, { FC, PropsWithChildren } from 'react';
import * as Dialog from '@radix-ui/react-dialog';
import cn from 'classnames';
import s from './drawer.module.scss';
import { StaggeredText } from 'react-scale/components/pages/zeitgeist/primitives/staggered-text';
import HubspotForm from 'react-hubspot-form';
import Button from 'react-scale/components/elements/button';

const FORM_ID = '31430479-09fd-432c-880e-053d522f71bb';

export const Drawer: FC<PropsWithChildren> = ({ children }) => {
  const [formLoaded, setFormLoaded] = React.useState(false);
  const [formSubmitted, setFormSubmitted] = React.useState(false);

  React.useEffect(() => {
    const hubSpotEventHandler = async event => {
      const data = event?.data;

      if (data && data.type === 'hsFormCallback' && data.eventName === 'onFormSubmit') {
        if (data.id === FORM_ID) {
          setFormSubmitted(true);
        }
      }
    };

    window.addEventListener('message', hubSpotEventHandler);

    return () => window.removeEventListener('message', hubSpotEventHandler);
  }, []);

  return (
    <Dialog.Root
      onOpenChange={open => {
        if (!open) {
          setFormLoaded(false);
        }
      }}
    >
      <Dialog.Trigger asChild>{children}</Dialog.Trigger>
      <Dialog.Portal>
        <Dialog.Overlay className={s['overlay']} />
        <Dialog.Content className={s.drawer}>
          <Dialog.Title asChild className={cn(s.drawer__title, 'hidden lg:block')}>
            <StaggeredText as="h5" rootMargin="0%" delay={0.1}>
              Confirm your attendance at Scale Summit 2023
            </StaggeredText>
          </Dialog.Title>
          <Dialog.Title
            asChild
            className={cn(s.drawer__title, 'lg:hidden !visible !max-w-[calc(100%_-_43px)]')}
          >
            <h5>Confirm your attendance at Scale Summit 2023</h5>
          </Dialog.Title>

          <div className={cn(s.form__wrapper, formLoaded && s['form__wrapper--loaded'])}>
            <div className={s.form}>
              <div
                className={cn(
                  !formSubmitted ? 'opacity-100' : 'opacity-0 absolute w-full',
                  'transition-opacity duration-700 ease-out'
                )}
              >
                <HubspotForm
                  onReady={() => setFormLoaded(true)}
                  portalId="4823527"
                  formId={FORM_ID}
                />
              </div>

              {formSubmitted && (
                <>
                  <StaggeredText
                    as="p"
                    className={cn('hidden lg:block', s['submitted-message'])}
                    delay={0.7}
                  >
                    Thank you for your registration! You'll received a confirmation email and
                    additional details soon.
                  </StaggeredText>
                  <p className={cn('lg:hidden', s['submitted-message'])}>
                    Thank you for your registration! You'll received a confirmation email and
                    additional details soon.
                  </p>
                  <svg
                    className={s.logo}
                    viewBox="0 0 93 38"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      fillRule="evenodd"
                      clipRule="evenodd"
                      d="M93 37.806H55.8V.606H93v37.2Zm-30.4-3.988h26.412V7.406L62.6 33.818ZM86.2 4.594H59.788v26.412L86.2 4.594ZM18.6 19.206l18.6 18.6V.606H0l18.6 18.6Zm-9.3 9.3 9.3 9.3v-18.6H0l9.3 9.3Zm0 0H0l9.3 9.3v-9.3Z"
                      fill="url(#paint0_linear_303_10701)"
                    />
                    <defs>
                      <linearGradient
                        id="paint0_linear_303_10701"
                        x1="21.335"
                        y1="-3.765"
                        x2="33.255"
                        y2="52.001"
                        gradientUnits="userSpaceOnUse"
                      >
                        <stop stopColor="#000" />
                        <stop offset="1" stopColor="#000" stopOpacity="0" />
                      </linearGradient>
                    </defs>
                  </svg>
                </>
              )}

              {/* @ts-expect-error Optional props not passed */}
              <Button
                type="primary"
                variant="black"
                typeButton={{
                  className: cn(
                    s.form__submit,
                    formSubmitted ? 'absolute opacity-0 pointer-events-none' : 'opacity-100',
                    'transition-opacity duration-700 ease-out w-full !ml-0 lg:!ml-auto lg:!mr-0 md:w-fit'
                  )
                }}
                text="Save my spot"
                onClick={() =>
                  (
                    document.querySelector(
                      `form#hsForm_${FORM_ID} > * input.hs-button`
                    ) as HTMLInputElement
                  ).click()
                }
              />
            </div>
          </div>

          <Dialog.Close className={s.drawer__close} aria-label="close" />
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
};
